import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem
} from '~/components/UI/shadcn/carousel'
import AutoScroll from 'embla-carousel-auto-scroll'
import { useState, useEffect } from 'react'
import sanitizeHtml from 'sanitize-html'
import { cva, VariantProps } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { useReducedMotion } from 'motion/react'
import { useMediaQuery } from 'usehooks-ts'

const tickerVariants = cva('', {
  variants: {
    colour: {
      'light-blue': 'bg-blue-l',
      chartreuse: 'bg-char',
      'light-pink': 'bg-pink',
      'light-aqua': 'bg-green',
      'light-orange': 'bg-orange',
      'light-purple': 'bg-purple'
    }
  },
  defaultVariants: {
    colour: 'light-blue'
  }
})

type TickerProps = {
  content: {
    configTickerItems: Array<{
      copy: string
      internalLink?: Entry
      externalLink?: string
      newWindow?: boolean
    }>
    colour: VariantProps<typeof tickerVariants>['colour']
    copy: string
    link: Entry
  }
}

const Ticker = ({ content }: TickerProps) => {
  const { colour } = content

  const [api, setApi] = useState<CarouselApi>()
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    if (!api) {
      return
    }
  }, [api])

  const isMassive = useMediaQuery('(min-width: 1800px)', {
    defaultValue: true,
    initializeWithValue: false
  })
  const isDesktop = useMediaQuery('(min-width: 1280px)', {
    defaultValue: true,
    initializeWithValue: false
  })
  const isTablet = useMediaQuery('(max-width: 1280px)', {
    defaultValue: true,
    initializeWithValue: false
  })
  const isMobile = useMediaQuery('(max-width: 640px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  let slides = [...content.configTickerItems]

  if (slides.length < 6) {
    if (
      (isDesktop && slides.length >= 4) ||
      (isTablet && slides.length >= 3) ||
      (isMobile && slides.length === 2)
    ) {
      slides = [...slides, ...slides]
    } else if (
      ((isDesktop || isTablet) && slides.length >= 2) ||
      (isMobile && slides.length === 1)
    ) {
      slides = [...slides, ...slides, ...slides]
    } else if (isTablet && slides.length === 1) {
      slides = [...slides, ...slides, ...slides, ...slides, ...slides]
    } else if (isMassive && slides.length === 1) {
      slides = [
        ...slides,
        ...slides,
        ...slides,
        ...slides,
        ...slides,
        ...slides,
        ...slides,
        ...slides,
        ...slides
      ]
    } else if (slides.length === 1) {
      slides = [
        ...slides,
        ...slides,
        ...slides,
        ...slides,
        ...slides,
        ...slides,
        ...slides
      ]
    }
  }

  return (
    <section className={cn(tickerVariants({ colour }))}>
      <Carousel
        setApi={setApi}
        opts={{
          loop: true,
          align: prefersReducedMotion ? 'center' : 'start',
          watchDrag: false
        }}
        plugins={[
          AutoScroll({
            playOnInit: prefersReducedMotion ? false : true,
            stopOnInteraction: prefersReducedMotion ? true : false,
            stopOnMouseEnter: true,
            startDelay: 300,
            speed: 1
          })
        ]}
      >
        <CarouselContent className='py-2xs'>
          {slides.map((slide, index) => (
            <CarouselItem
              key={`ticker-${index}`}
              className='lg:gap-m sm:gap-s gap-xs sm:!pl-s lg:!pl-m flex max-w-fit min-w-min items-center'
            >
              <a
                {...(slide.internalLink?.sys?.uri
                  ? { href: slide.internalLink.sys.uri }
                  : { href: slide.externalLink })}
                {...(slide.externalLink && slide.newWindow
                  ? { target: '_blank' }
                  : {})}
                {...(slide.externalLink && slide.newWindow
                  ? { rel: 'noreferrer nofollow' }
                  : {})}
                aria-label={slide?.copy}
                className='tag unstyled-link hover:text-blue-d w-full text-center whitespace-nowrap hover:underline'
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(slide.copy, {
                    allowedTags: ['em', 'strong']
                  })
                }}
              ></a>

              <svg width='4' height='4'>
                <rect width='4' height='4' className='fill-blue-m' />
              </svg>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </section>
  )
}

export default Ticker
